import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import hospitalBg from "../assets/hospital.jpeg";
import { getFirstAccessibleAdminPath } from "../utils/adminAccess";

const AdminLogin = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState("admin"); // admin or employee
  const navigate = useNavigate();

const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      // 1. Choose endpoint based on toggle
      const endpoint =
        loginType === "admin"
          ? "/api/adminManagement/login"
          : "/api/employee-ids/login";

      const res = await axios.post(endpoint, {
        userId: userId.trim(), // This will be NEH0010 for employees
        password: password.trim(),
      });

      if (res.data.token) {
        // 2. Get user data (Simplified to check both possibilities)
        const user = res.data.user || res.data.admin; 
        
        if (!user) {
          setError("Login failed. Invalid response from server.");
          return;
        }

        localStorage.setItem("token", res.data.token);

      
        if (loginType === "employee") {
      localStorage.removeItem("adminData");
      localStorage.setItem("employeeToken", res.data.token);
      localStorage.setItem(
        "employeeUser",
        JSON.stringify({
      employeeID: user.employeeID || user.employeeId, // Matches NEH0010
      employeeUserId: user.employeeUserId,           // THIS IS THE LINE YOU NEED FOR YOUR WORK
          name: user.name,
          email: user.email,
          role: "employee",
          designation: user.designation,                 // Optional: add if you need it
          department: user.department                    // Optional: add if you need it
        })
      );
        } else {
          localStorage.removeItem("employeeUser");
          localStorage.removeItem("employeeToken");
          localStorage.setItem("adminData", JSON.stringify(user));
        }

        localStorage.setItem("userPermissions", JSON.stringify(user.permissions || []));
        navigate(
          loginType === "admin"
            ? getFirstAccessibleAdminPath(user, user.permissions || [])
            : "/AttendanceSignIn"
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message;
      if (!status) {
        setError("Cannot reach server. Check deployed API URL / network.");
      } else {
        setError(backendMessage || `Login failed (${status}).`);
      }
    }
  };
  return (
    <div 
      className="h-screen w-full flex justify-center items-end pb-10 bg-no-repeat bg-cover bg-bottom bg-fixed relative"
      style={{ backgroundImage: `url(${hospitalBg})` }}
    >
      {/* Subtle overlay to help the card stand out */}
      <div className="absolute inset-0 bg-black/30"></div>

      <div className="relative z-10 bg-white/10 backdrop-blur-xl py-3 px-8 rounded-2xl shadow-2xl w-[90%] sm:w-[420px] border border-white/20">
      <div className="text-center mb-1">
        <h2 className="text-2xl font-black text-white tracking-tighter sm:tracking-normal drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">
         NETRAPEDIA EYE HOSPITAL
        </h2>
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-purple-500 to-transparent mt-1"></div>
      </div>
        {/* Heading */}
        <h1 className="text-lg font-semibold text-white/90 text-center mb-2">
          {loginType === "admin" ? "Admin Login" : "Employee Login"}
        </h1>
        {/* Error */}
        {error && <p className="text-red-400 text-center mb-3 text-sm">{error}</p>}

        {/* Toggle Buttons */}
        <div className="flex flex-col sm:flex-row justify-center mb-5 gap-3 sm:gap-4">
          <button
            type="button"
            className={`px-4 py-2 rounded-lg font-semibold ${
              loginType === "admin"
                ? "bg-purple-600 text-white"
                : "bg-white/20 text-white"
            }`}
            onClick={() => setLoginType("admin")}
          >
            Admin
          </button>

          <button
            type="button"
            className={`px-4 py-2 rounded-lg font-semibold ${
              loginType === "employee"
                ? "bg-purple-600 text-white"
                : "bg-white/20 text-white"
            }`}
            onClick={() => setLoginType("employee")}
          >
            Employee
          </button>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-5">
          {/* User ID */}
          <div>
            <label className="text-white block mb-1">User ID</label>
         
          <input
            type="text"
            placeholder={loginType === "admin" ? "Enter Admin ID" : "Enter Employee ID"}
            value={userId}
            onChange={(e) => {
              let value = e.target.value;
              if (loginType === "employee") {
                value = value.toUpperCase(); // Keep it Capital, but NO HYPHEN logic
              }
              setUserId(value);
            }}
            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-gray-600 text-white ..."
            required
          />
          </div>

          {/* Password */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ 
                WebkitBoxShadow: "0 0 0px 1000px rgba(0,0,0,0) inset", 
                transition: "background-color 5000s ease-in-out 0s",
                WebkitTextFillColor: "white" 
              }}
              className="w-full px-4 py-2 rounded-lg bg-gray-600 text-white placeholder-gray-200 outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
            <button
              type="button"
              className="absolute right-2 sm:right-3 top-2 sm:top-3 text-white text-lg sm:text-xl" // Changed to white for better visibility
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold shadow-lg hover:scale-105 transform transition duration-300"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
